import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Clock, User, Mail, MessageSquare, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { publicBookingApi } from '../services/api';
import CookieBanner from '../components/CookieBanner';
import { useToast } from '../context/ToastContext';
import './Pages.css';

interface CoachInfo {
  first_name: string;
  last_name: string;
  title: string | null;
  business_name: string | null;
  profile_image_url: string | null;
}

interface Slot {
  id: string;
  start_time: string;
  end_time: string;
  location_type: string;
}

const monthNames = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

export default function BookingPage() {
  const { slug } = useParams<{ slug: string }>();
  const { showToast } = useToast();
  const [coach, setCoach] = useState<CoachInfo | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Booking flow state
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [step, setStep] = useState<'calendar' | 'form' | 'confirmed'>('calendar');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Form state
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestMessage, setGuestMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bookingRef, setBookingRef] = useState('');

  useEffect(() => {
    if (!slug) return;
    loadData();
  }, [slug]); // eslint-disable-line

  const loadData = async () => {
    setLoading(true);
    try {
      const [coachRes, slotsRes] = await Promise.all([
        publicBookingApi.getCoachInfo(slug!),
        publicBookingApi.getSlots(slug!),
      ]);
      setCoach(coachRes.data);
      setSlots(slotsRes.data.slots);
    } catch {
      setError('Coach nicht gefunden oder Buchungsseite nicht verfügbar.');
    } finally {
      setLoading(false);
    }
  };

  const getSlotsForDate = (dateStr: string) => {
    return slots.filter((s) => s.start_time.startsWith(dateStr));
  };

  const getAvailableDates = () => {
    const dates = new Set<string>();
    slots.forEach((s) => dates.add(s.start_time.split('T')[0]));
    return dates;
  };

  const availableDates = getAvailableDates();

  const handleSlotSelect = (slot: Slot) => {
    setSelectedSlot(slot);
    setStep('form');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot || !slug) return;
    setSubmitting(true);
    try {
      const res = await publicBookingApi.book(slug, {
        booking_slot_id: selectedSlot.id,
        guest_name: guestName,
        guest_email: guestEmail,
        guest_message: guestMessage || undefined,
      });
      setBookingRef(res.data.booking_reference);
      setStep('confirmed');
    } catch (err: any) {
      showToast(err.response?.data?.detail || 'Buchung fehlgeschlagen. Bitte versuche es erneut.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // Calendar generation
  const generateMonthGrid = () => {
    const y = currentMonth.getFullYear();
    const m = currentMonth.getMonth();
    const first = new Date(y, m, 1);
    const startDay = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(y, m + 1, 0).getDate();

    const days: { date: Date; isCurrentMonth: boolean }[] = [];
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({ date: new Date(y, m, -i), isCurrentMonth: false });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(y, m, i), isCurrentMonth: true });
    }
    while (days.length % 7 !== 0) {
      const last = days[days.length - 1].date;
      const next = new Date(last);
      next.setDate(next.getDate() + 1);
      days.push({ date: next, isCurrentMonth: false });
    }
    return days;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        minHeight: '100vh', background: 'var(--bg)', color: 'var(--text-muted)',
      }}>
        Wird geladen...
      </div>
    );
  }

  if (error || !coach) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        minHeight: '100vh', background: 'var(--bg)',
      }}>
        <div style={{
          background: 'var(--bg-card)', padding: 40, borderRadius: 12, textAlign: 'center',
          maxWidth: 400, boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        }}>
          <Calendar size={40} style={{ color: 'var(--text-muted)', marginBottom: 16 }} />
          <h2 style={{ marginBottom: 8 }}>Seite nicht gefunden</h2>
          <p style={{ color: 'var(--text-muted)' }}>{error}</p>
        </div>
      </div>
    );
  }

  const coachDisplayName = coach.business_name || `${coach.first_name} ${coach.last_name}`;
  const today = new Date().toISOString().split('T')[0];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg, #f5f1eb)', padding: '40px 20px' }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        {/* Coach Header */}
        <div style={{
          background: `linear-gradient(135deg, var(--primary-dark, #5D4E37), var(--primary, #8B7355))`,
          color: '#fff', padding: '32px 40px', borderRadius: '12px 12px 0 0',
          textAlign: 'center',
        }}>
          <h1 style={{ margin: 0, fontSize: '1.5rem' }}>{coachDisplayName}</h1>
          {coach.title && <p style={{ margin: '4px 0 0', opacity: 0.8 }}>{coach.title}</p>}
          <p style={{ margin: '12px 0 0', opacity: 0.7, fontSize: '0.9rem' }}>Termin online buchen</p>
        </div>

        <div style={{
          background: 'var(--bg-card)', padding: '32px 40px', borderRadius: '0 0 12px 12px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        }}>

          {/* Step 1: Calendar */}
          {step === 'calendar' && (
            <>
              <h2 style={{ fontSize: '1.1rem', marginBottom: 16 }}>
                <Calendar size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Wähle einen Termin
              </h2>

              {slots.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                  <Clock size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                  <p>Momentan sind keine freien Termine verfügbar.</p>
                </div>
              ) : (
                <>
                  {/* Mini Calendar */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <button
                        onClick={() => {
                          const d = new Date(currentMonth);
                          d.setMonth(d.getMonth() - 1);
                          setCurrentMonth(d);
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <strong>{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</strong>
                      <button
                        onClick={() => {
                          const d = new Date(currentMonth);
                          d.setMonth(d.getMonth() + 1);
                          setCurrentMonth(d);
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, textAlign: 'center' }}>
                      {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((d) => (
                        <div key={d} style={{ fontSize: '0.75rem', color: 'var(--text-muted)', padding: '4px 0' }}>{d}</div>
                      ))}
                      {generateMonthGrid().map(({ date: d, isCurrentMonth }) => {
                        const dateStr = d.toISOString().split('T')[0];
                        const hasSlots = availableDates.has(dateStr);
                        const isSelected = dateStr === selectedDate;
                        const isPast = dateStr < today;
                        return (
                          <button
                            key={dateStr}
                            onClick={() => hasSlots && !isPast ? setSelectedDate(dateStr) : null}
                            disabled={!hasSlots || isPast}
                            style={{
                              padding: '8px 4px',
                              border: isSelected ? '2px solid var(--primary-dark, #5D4E37)' : '1px solid transparent',
                              borderRadius: 8,
                              background: isSelected ? '#f0ebe3' : hasSlots && !isPast ? 'var(--bg-hover)' : 'transparent',
                              color: !isCurrentMonth ? 'var(--text-muted)' : isPast ? 'var(--text-muted)' : hasSlots ? 'var(--text)' : 'var(--text-muted)',
                              cursor: hasSlots && !isPast ? 'pointer' : 'default',
                              fontWeight: isSelected ? 700 : 400,
                              fontSize: '0.9rem',
                            }}
                          >
                            {d.getDate()}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Time Slots for selected date */}
                  {selectedDate && (
                    <div>
                      <h3 style={{ fontSize: '0.95rem', marginBottom: 10 }}>
                        Verfügbare Zeiten — {fmtDate(selectedDate + 'T00:00:00')}
                      </h3>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {getSlotsForDate(selectedDate).map((slot) => (
                          <button
                            key={slot.id}
                            onClick={() => handleSlotSelect(slot)}
                            style={{
                              padding: '10px 18px',
                              border: '1px solid var(--border)',
                              borderRadius: 8,
                              background: 'var(--bg-card)',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: 500,
                              transition: 'all 0.15s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'var(--primary-dark, #5D4E37)';
                              e.currentTarget.style.color = '#fff';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'var(--bg-card)';
                              e.currentTarget.style.color = 'var(--text)';
                            }}
                          >
                            {fmtTime(slot.start_time)} – {fmtTime(slot.end_time)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Step 2: Booking Form */}
          {step === 'form' && selectedSlot && (
            <>
              <button
                onClick={() => { setStep('calendar'); setSelectedSlot(null); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--primary-dark, #5D4E37)', fontWeight: 500, padding: 0, marginBottom: 16,
                  display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.9rem',
                }}
              >
                <ChevronLeft size={16} /> Zurück zur Terminauswahl
              </button>

              <div style={{
                padding: '14px 18px', background: 'var(--bg-hover)', borderRadius: 8,
                borderLeft: '4px solid var(--primary-dark, #5D4E37)', marginBottom: 24,
              }}>
                <div style={{ fontWeight: 600 }}>{fmtDate(selectedSlot.start_time)}</div>
                <div style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>
                  {fmtTime(selectedSlot.start_time)} – {fmtTime(selectedSlot.end_time)} Uhr
                </div>
              </div>

              <h2 style={{ fontSize: '1.1rem', marginBottom: 16 }}>
                <User size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Deine Kontaktdaten
              </h2>

              <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.9rem' }}>
                      <User size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Name *
                    </label>
                    <input
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="Vor- und Nachname"
                      required
                      style={{
                        width: '100%', padding: '10px 14px', border: '1px solid var(--border)',
                        borderRadius: 8, fontSize: '0.95rem',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.9rem' }}>
                      <Mail size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> E-Mail *
                    </label>
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      placeholder="deine@email.de"
                      required
                      style={{
                        width: '100%', padding: '10px 14px', border: '1px solid var(--border)',
                        borderRadius: 8, fontSize: '0.95rem',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: '0.9rem' }}>
                      <MessageSquare size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Nachricht (optional)
                    </label>
                    <textarea
                      value={guestMessage}
                      onChange={(e) => setGuestMessage(e.target.value)}
                      placeholder="Was möchten Sie besprechen?"
                      rows={3}
                      style={{
                        width: '100%', padding: '10px 14px', border: '1px solid var(--border)',
                        borderRadius: 8, fontSize: '0.95rem', resize: 'vertical',
                      }}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    width: '100%', marginTop: 24, padding: '14px',
                    background: 'var(--primary-dark, #5D4E37)', color: '#fff', border: 'none',
                    borderRadius: 8, fontSize: '1rem', fontWeight: 600,
                    cursor: submitting ? 'wait' : 'pointer',
                    opacity: submitting ? 0.7 : 1,
                  }}
                >
                  {submitting ? 'Wird gebucht...' : 'Termin buchen'}
                </button>
              </form>
            </>
          )}

          {/* Step 3: Confirmation */}
          {step === 'confirmed' && selectedSlot && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{
                width: 60, height: 60, borderRadius: '50%', background: 'var(--bg-hover)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <Check size={30} color="#2e7d32" />
              </div>
              <h2 style={{ marginBottom: 8 }}>Termin angefragt!</h2>
              <p style={{ color: 'var(--text-light)', marginBottom: 24 }}>
                Deine Buchungsanfrage wurde erfolgreich übermittelt. Du erhältst eine Bestätigung per E-Mail.
              </p>

              <div style={{
                padding: '18px 24px', background: 'var(--bg-hover)', borderRadius: 8,
                textAlign: 'left', marginBottom: 24,
              }}>
                <div style={{ marginBottom: 8 }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Referenz</span>
                  <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>{bookingRef}</div>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Termin</span>
                  <div style={{ fontWeight: 500 }}>{fmtDate(selectedSlot.start_time)}</div>
                  <div style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>
                    {fmtTime(selectedSlot.start_time)} – {fmtTime(selectedSlot.end_time)} Uhr
                  </div>
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>Coach</span>
                  <div style={{ fontWeight: 500 }}>{coachDisplayName}</div>
                </div>
              </div>

              <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                Der Coach wird deine Anfrage prüfen und den Termin bestätigen.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 24, color: 'var(--text-muted)', fontSize: '0.75rem' }}>
          {coachDisplayName || 'CoachDesk365'}
          <div style={{ marginTop: 6 }}>
            <a href="/impressum" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-muted)', marginRight: 12 }}>Impressum</a>
            <a href="/datenschutz" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-muted)' }}>Datenschutz</a>
          </div>
        </div>
      </div>
      <CookieBanner />
    </div>
  );
}
